import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Calendar as CalendarIcon, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const schema = z.object({
  nom: z.string().min(2, "Nom requis"),
  prenom: z.string().min(2, "Prénom requis"),
  telephone: z.string().min(8, "Téléphone invalide"),
  email: z.string().email("Email invalide"),
  immatriculation: z.string().optional(),
  vehicule: z.string().optional(),
  service: z.string().min(1, "Choisissez un service"),
  date_souhaitee: z.string().min(1, "Date requise"),
  message: z.string().optional(),
});

const SERVICES = [
  "Entretien & Révision", "Vidange", "Freinage", "Pneumatiques",
  "Diagnostic électronique", "Climatisation", "Batterie", "Distribution",
  "Carrosserie", "Préparation contrôle technique", "Hybride & Électrique",
  "Vitrage automobile", "Calibration ADAS", "Autre",
];

export default function AppointmentPage() {
  const [params] = useSearchParams();
  const presetService = params.get("service") || "";
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { service: presetService },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await api.post("/appointments", data);
      setSubmitted(true);
      toast.success("Rendez-vous envoyé !", { description: "Nous vous recontactons sous 24h." });
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-32 pb-20 px-6 md:px-12 min-h-[70vh] flex items-center bg-[#F8F9FA]">
        <div className="max-w-xl mx-auto text-center bg-white border border-[#E5E7EB] p-10 md:p-16">
          <CheckCircle size={64} weight="duotone" className="text-[#E63946] mx-auto" />
          <h1 className="mt-6 text-3xl font-bold text-[#0D2C54]">Demande envoyée</h1>
          <p className="mt-4 text-[#555555]">Merci ! Notre équipe vous recontacte sous 24h pour confirmer votre créneau.</p>
          <Link to="/"><Button data-testid="rdv-back-home" className="mt-8 bg-[#0D2C54] hover:bg-[#0A2242] text-white rounded-none h-12 px-8">Retour à l'accueil</Button></Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Prise de rendez-vous en ligne — MECA AUTO 17 Tonnay-Charente"
        description="Réservez votre rendez-vous chez MECA AUTO 17 à Tonnay-Charente en quelques clics. Entretien, diagnostic, réparation : choisissez votre service et votre créneau."
      />
      <section className="pt-32 pb-20 px-6 md:px-12 bg-[#F8F9FA]">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-[#E63946]">Rendez-vous</span>
            <h1 className="mt-4 text-4xl lg:text-5xl font-bold text-[#0D2C54] tracking-tight">
              Réservez votre intervention.
            </h1>
            <p className="mt-6 text-[#555555] leading-relaxed">
              Remplissez le formulaire ci-contre, nous revenons vers vous sous 24h pour confirmer le créneau et préciser les modalités d'accueil.
            </p>
            <div className="mt-10 space-y-4">
              {[
                "Confirmation par téléphone sous 24h",
                "Devis transparent avant intervention",
                "Véhicule de prêt sur demande",
                "Aucun engagement à la prise de rendez-vous",
              ].map((b) => (
                <div key={b} className="flex items-center gap-3 text-sm">
                  <CheckCircle size={20} weight="fill" className="text-[#E63946]" />
                  <span className="text-[#333333]">{b}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} data-testid="appointment-form" className="lg:col-span-7 bg-white border border-[#E5E7EB] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-[#E5E7EB]">
              <CalendarIcon size={24} className="text-[#E63946]" weight="duotone" />
              <h2 className="font-display text-xl font-semibold text-[#0D2C54]">Vos informations</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nom *" error={errors.nom}>
                <Input data-testid="appt-nom" {...register("nom")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Prénom *" error={errors.prenom}>
                <Input data-testid="appt-prenom" {...register("prenom")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Téléphone *" error={errors.telephone}>
                <Input data-testid="appt-tel" {...register("telephone")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Email *" error={errors.email}>
                <Input data-testid="appt-email" type="email" {...register("email")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Immatriculation" error={errors.immatriculation}>
                <Input data-testid="appt-immat" placeholder="AB-123-CD" {...register("immatriculation")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Marque & modèle" error={errors.vehicule}>
                <Input data-testid="appt-vehicule" placeholder="Ex: Renault Clio IV" {...register("vehicule")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Service *" error={errors.service}>
                <Select onValueChange={(v) => setValue("service", v)} defaultValue={presetService}>
                  <SelectTrigger data-testid="appt-service" className="rounded-none border-[#E5E7EB] h-12">
                    <SelectValue placeholder="Choisir un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" {...register("service")} value={watch("service") || ""} />
              </Field>
              <Field label="Date souhaitée *" error={errors.date_souhaitee}>
                <Input data-testid="appt-date" type="date" {...register("date_souhaitee")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Message complémentaire" error={errors.message}>
                <Textarea data-testid="appt-message" {...register("message")} rows={4} className="rounded-none border-[#E5E7EB] resize-none" placeholder="Précisions sur l'intervention souhaitée..." />
              </Field>
            </div>

            <Button
              type="submit"
              data-testid="appt-submit"
              disabled={loading}
              className="mt-8 w-full bg-[#E63946] hover:bg-[#D62839] text-white rounded-none h-14 font-semibold tracking-wide"
            >
              {loading ? "Envoi..." : "Réserver mon rendez-vous"}
            </Button>
            <p className="mt-4 text-xs text-[#555555] text-center">En soumettant, vous acceptez d'être recontacté pour confirmer votre rendez-vous.</p>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs overline text-[#0D2C54] mb-2 block">{label}</Label>
      {children}
      {error && <p className="text-xs text-[#E63946] mt-1">{error.message}</p>}
    </div>
  );
}
